import { ReactNode } from 'react';

import { mergeClasses } from '@/utils';

import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  IButton,
} from '@/components/common';

interface IVerticalBaseModal {
  open: boolean;
  onOpen: (open: boolean) => void;
  title: string;
  subtitle?: ReactNode;
  children?: ReactNode;
  icon?: ReactNode;

  hideFooter?: boolean;

  isOkButtonHidden?: boolean;
  okButtonLabel?: ReactNode;
  okButtonColor?: IButton['buttonColor'];
  onOk?: () => void;
  okButtonClassName?: string;
  isOkButtonDisabled?: boolean;

  isCancelButtonHidden?: boolean;
  cancelButtonText?: string;
  onCancel?: () => void;
  cancelButtonClassName?: string;
  isCancelButtonDisabled?: boolean;

  // Customization
  contentClassName?: string;

  // Behavior
  closeOnEscape?: boolean;
}

const VerticalBaseModal = ({
  open,
  onOpen,
  title,
  icon,
  subtitle,
  children,
  hideFooter = false,
  isOkButtonHidden = false,
  okButtonLabel = 'Continue',
  okButtonColor = 'primary',
  onOk,
  okButtonClassName,
  isOkButtonDisabled = false,
  isCancelButtonHidden = false,
  cancelButtonText = 'Cancel',
  onCancel,
  cancelButtonClassName,
  isCancelButtonDisabled = false,
  contentClassName,
  closeOnEscape = true,
}: IVerticalBaseModal) => {
  return (
    <Dialog open={open} onOpenChange={onOpen}>
      <DialogContent
        showCloseButton={false}
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => {
          if (!closeOnEscape) e.preventDefault();
        }}
        className={mergeClasses('flex flex-col rounded-2xl p-6 bg-white', contentClassName)}
      >
        <DialogHeader className="w-full flex !flex-row items-start justify-between">
          <div className="flex flex-col justify-center items-center gap-6 w-full">
            {icon}
            <div className="flex gap-6">
              <DialogTitle className="flex-1 text-neutral-9 !text-h5 !font-bold">
                {title}
              </DialogTitle>
            </div>
            {subtitle && (
              <span className="!text-body-sm !text-neutral-7 !font-medium text-center">
                {subtitle}
              </span>
            )}
          </div>
        </DialogHeader>

        {children && <div className="flex-1 w-full">{children}</div>}

        {!hideFooter && (
          <DialogFooter className="w-full gap-4 mt-2 flex !flex-col items-center justify-center">
            {!isOkButtonHidden && (
              <Button
                buttonStyle="filled"
                buttonColor={okButtonColor}
                borderRadius="soft"
                size="lg"
                disabled={isOkButtonDisabled}
                className={mergeClasses('w-full', okButtonClassName)}
                onClick={onOk}
              >
                {okButtonLabel}
              </Button>
            )}
            {!isCancelButtonHidden && (
              <Button
                buttonStyle="outline"
                buttonColor="neutral"
                borderRadius="soft"
                size="lg"
                disabled={isCancelButtonDisabled}
                className={mergeClasses('w-full', cancelButtonClassName)}
                onClick={onCancel || (() => onOpen(false))}
              >
                {cancelButtonText}
              </Button>
            )}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default VerticalBaseModal;
